import { useState, useEffect } from "react";
import { BgOrbs } from "@/components/shared/BgOrbs";

const SESSION_KEY = "portfolio-auth";

export function PasswordGate({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<"loading" | "open" | "locked" | "authed">("loading");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch("/api/auth")
      .then((r) => r.json())
      .then(({ required }) => {
        if (!required) {
          setStatus("open");
        } else if (sessionStorage.getItem(SESSION_KEY) === "1") {
          setStatus("authed");
        } else {
          setStatus("locked");
        }
      })
      .catch(() => setStatus("open"));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/api/auth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        sessionStorage.setItem(SESSION_KEY, "1");
        setStatus("authed");
      } else {
        setError("That's not it — try again.");
      }
    } catch {
      setError("Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "loading") return null;
  if (status === "open" || status === "authed") return <>{children}</>;

  return (
    <div className="relative min-h-screen bg-[#F7F8FA] flex items-center justify-center px-6 overflow-hidden">
      <BgOrbs variant="default" />

      <div className="relative flex flex-col items-center text-center max-w-sm w-full">
        {/* Name */}
        <p className="font-[Bricolage_Grotesque] font-bold text-[#1A1A1A] text-sm tracking-tight mb-10">
          Verónica Aguilar
        </p>

        {/* Heading */}
        <h1 className="font-[Bricolage_Grotesque] font-bold text-[#1A1A1A] text-4xl leading-tight tracking-tight mb-3">
          You got the link —<br />
          <span className="text-[#1D3FE8]">just one more step.</span>
        </h1>

        <p className="text-[#888888] font-[DM_Sans] text-base leading-relaxed mb-10">
          Verónica shared this with you directly.<br />
          Enter the password she gave you to continue.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="w-full flex flex-col gap-3">
          <input
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError("");
            }}
            placeholder="Password"
            autoFocus
            className="w-full border border-[#E2E8F0] rounded-full px-5 py-3 text-sm font-[DM_Sans] text-[#1A1A1A] placeholder:text-[#BBBBBB] bg-white outline-none focus:border-[#1D3FE8] transition-colors"
          />
          {error && (
            <p className="text-sm font-[DM_Sans] text-red-400">{error}</p>
          )}
          <button
            type="submit"
            disabled={submitting || !password}
            className="w-full px-6 py-3 rounded-full bg-[#1D3FE8] text-white text-sm font-medium font-[DM_Sans] hover:bg-[#1532c0] transition-colors disabled:opacity-40"
          >
            {submitting ? "Checking…" : "Let me in"}
          </button>
        </form>
      </div>
    </div>
  );
}
